// JOBE-0098.3: state 4 — matches sub-section. Renders the host's
// onSearch() promise resolution. Result rows reuse the
// LibraryRow-style hairline aesthetic; a Phase 4 follow-up extracts a
// shared row template.

import { useEffect, useState } from 'react';

// 200 ms debounce on the parsed-key change. Library queries are not
// cheap; bursting a fresh BFF round-trip per keystroke is wasteful.
// Keyed on the raw input + serialised predicates rather than the
// parsed object identity (a fresh object per render would defeat the
// debounce).
const SEARCH_DEBOUNCE_MS = 200;

function parsedKey(parsed) {
  if (!parsed) return '';
  const preds = (parsed.predicates || [])
    .map((p) => `${p.friendlyToken}${p.op}${(p.values || []).join(',')}`)
    .join('|');
  return `${parsed.raw || ''}::${preds}::${parsed.fuzzyFragment || ''}`;
}

export function MatchesSection({
  parsed,
  onSearch,
  highlightIndex,
  onHighlight,
  onPick,
}) {
  const [state, setState] = useState({ status: 'idle', rows: [] });
  const key = parsedKey(parsed);

  useEffect(() => {
    if (!onSearch) {
      setState({ status: 'idle', rows: [] });
      return undefined;
    }
    let cancelled = false;
    // PR #700 review (LOW): pair the cancelled-flag with an
    // AbortController so the host can also short-circuit the underlying
    // fetch (not just the React state update). Hosts that ignore the
    // signal still work — it's purely additive.
    const controller = typeof AbortController === 'function'
      ? new AbortController()
      : null;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setState({ status: 'loading', rows: [] });
      Promise.resolve()
        .then(() => onSearch(parsed, { signal: controller?.signal }))
        .then((page) => {
          if (cancelled) return;
          const rows = Array.isArray(page?.rows)
            ? page.rows
            : Array.isArray(page?.items)
            ? page.items
            : [];
          setState({ status: 'ready', rows: rows.slice(0, 8) });
        })
        .catch((err) => {
          if (cancelled) return;
          // Aborted fetches surface as DOMException('AbortError'); they
          // aren't user-facing errors — swallow rather than flipping
          // the UI to the error state.
          if (err && (err.name === 'AbortError' || err.code === 20)) return;
          setState({ status: 'error', rows: [], error: err });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
    };
    // parsed is intentionally excluded — its identity changes per
    // render even when the key is stable. The key + onSearch capture
    // every relevant change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, onSearch]);

  return (
    <section className="omni-section omni-section-matches" aria-label="Matches">
      <h4 className="omni-section-title">Matches</h4>
      {state.status === 'loading' && (
        <p className="omni-section-empty">searching…</p>
      )}
      {state.status === 'error' && (
        <p className="omni-section-empty">search failed</p>
      )}
      {state.status === 'ready' && state.rows.length === 0 && (
        <p className="omni-section-empty">no matches</p>
      )}
      {state.status === 'ready' && state.rows.length > 0 && (
        <ul className="omni-opt-list" role="listbox">
          {state.rows.map((row, i) => {
            const isHigh = i === highlightIndex;
            const urn = row.urn || row.id;
            return (
              <li key={urn || i}>
                <button
                  type="button"
                  className={isHigh ? 'omni-opt omni-opt-high' : 'omni-opt'}
                  onMouseEnter={() => onHighlight && onHighlight(i)}
                  onClick={() => onPick && onPick(row, i)}
                  role="option"
                  aria-selected={isHigh}
                  data-urn={urn}
                >
                  <span className="omni-opt-label">
                    {row.displayName || row.title || row.name || urn}
                  </span>
                  {row.type && (
                    <code className="omni-opt-value">{row.type}</code>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
